import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import Piechart from "@/components/Piechart";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import {
  OPTION_ALL,
  hasNonAllOptionActive,
} from "@/components/pageCards/filterSort/constants";
import {
  createTimeFilters,
  getTenantCategoryOptions,
} from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { usePdfPreview } from "@/pages/home/pdfPreview";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";

import { getWasteTypesOptions } from "../../../pages/customer/dashboard/functions/filters";
import { pieWasteContribution } from "../../../pages/customer/dashboard/functions/pieChartData";

type WasteContributionFilters = "period" | "tenantCategory" | "wasteType";

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const PieWasteContribution: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const { t } = useTrans();

  const [
    {
      period: [selectedPeriod] = [],
      tenantCategory,
      wasteType: [selectedWasteType] = [],
    },
    setFilterState,
  ] = useState<ActiveOptions<WasteContributionFilters>>({} as any);
  const { wasteTypes } = useCommonEntitiesStore();
  const { currentTerminal } = useTerminalsStateRE();
  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const { pdfPreviewOpen } = usePdfPreview();
  const { setWasteContributionLoading } = useHomeLoadingState();
  const { usedTenantCategories } = useMinimalCustomers();

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, timeFilter],
  );

  const { startTimeISO, endTimeISO } = useMemo(() => {
    return getTimeFrame(
      selectedPeriod?.value as FilterTimeFrameValue,
      defaultPeriod,
    );
  }, [selectedPeriod, defaultPeriod]);

  const {
    data: currentPeriod,
    isLoading,
    isError,
  } = trpc.dwh.customers.get.useQuery(
    {
      startTimeISO,
      endTimeISO,
      terminalId: currentTerminal?.id || "",
      commercialCustomerCategory: hasNonAllOptionActive(tenantCategory)
        ? tenantCategory[0].value
        : undefined,
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          currentTerminal?.id,
          startTimeISO,
          endTimeISO,
          tenantCategory,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  useEffect(() => {
    setWasteContributionLoading(isLoading);
  }, [setWasteContributionLoading, isLoading]);

  const pieFilters: FilterSort[] = [
    createTimeFilters({ defaultValue: defaultPeriod }, [
      FilterTimeFrameValue.Last30Days,
      FilterTimeFrameValue.LastMonth,
      FilterTimeFrameValue.YearToDate,
      FilterTimeFrameValue.Last6FullMonths,
      FilterTimeFrameValue.LastFullYear,
    ]),
    {
      id: "tenantCategory",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      options: getTenantCategoryOptions(usedTenantCategories),
      title: "statisticsLabels:tenantCategory",
      defaultValue: OPTION_ALL,
    },
    {
      id: "wasteType",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      options: getWasteTypesOptions(currentPeriod, wasteTypes, wasteTypeConfig),
      title: "entities:wasteTypes",
      defaultValue: "all",
    },
  ];

  const pieWasteDistributionData = useMemo(
    () =>
      currentPeriod
        ? pieWasteContribution(
            currentPeriod,
            selectedWasteType?.value,
            t("statisticsLabels:otherTenants"),
          )
        : [],
    [currentPeriod, t, selectedWasteType],
  );

  const chart = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!pieWasteDistributionData.length) {
      return <EmptyView type="noDataForInterval" className="min-h-[300px]" />;
    }

    return (
      <Piechart
        data={pieWasteDistributionData}
        filterLabel={t("hints:clickToSelectOrUnselectTenants")}
      />
    );
  }, [isError, isLoading, pieWasteDistributionData, t]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setFilterState(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.WasteContribution,
        );
      }
    },
    [setFilterState, setTimeFilters],
  );

  return (
    <Card
      title={"statisticsLabels:wasteContributions"}
      filters={pieFilters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
      alwaysExpanded={pdfPreviewOpen}
    >
      {chart}
    </Card>
  );
};
